import { Auth } from "aws-amplify";

/**
 * Stale-while-revalidate, for one browser session: the last good result of a read, kept in
 * memory so a page revisited in the same session paints at once while it fetches again.
 *
 * Every entry belongs to the signed-in user who fetched it, and nothing crosses accounts:
 * - `peek` answers only when the entry's owner is the user signed in right now (read
 *   synchronously from the Cognito user pool), not merely the last user the cache saw;
 * - sign-in and sign-out (main.js) `clear` the cache and bump its generation, and a fetch that
 *   started in an older generation is dropped when it lands, so a slow response for a user who
 *   has since signed out is never stored for whoever signed in next;
 * - a different user's first fetch empties the cache.
 */
const entries = new Map();
let owner = null;
let generation = 0;

/** The signed-in username, or null when there is none (or Auth is unavailable, as in tests). */
export async function currentOwner() {
  try {
    const u = await Auth.currentAuthenticatedUser();
    return (u && u.username) || null;
  } catch (e) {
    return null;
  }
}

/** The user signed in right now, synchronously (the user pool's stored last user), or null. */
export function signedInUser() {
  try {
    const u = Auth.userPool && Auth.userPool.getCurrentUser();
    return (u && u.getUsername()) || null;
  } catch (e) {
    return null;
  }
}

/** The cached value for `key`, synchronously; undefined unless it is the signed-in user's. */
export function peek(key) {
  const e = entries.get(key);
  if (!e || !owner || e.owner !== owner) return undefined;
  return e.owner === signedInUser() ? e.data : undefined;
}

/**
 * Stores `data` for `key` as `who`'s, unless the cache was cleared since `gen` (the generation
 * the fetch started in) or `accept(current, data)` says the cached value is newer. A different
 * user empties the cache first.
 */
export function put(key, data, who, gen = generation, accept = null) {
  if (gen !== generation || !who || data === undefined || data === null) return;
  if (who !== owner) {
    entries.clear();
    owner = who;
  }
  const cur = entries.get(key);
  if (cur && accept && !accept(cur.data, data)) return;
  entries.set(key, { owner: who, data });
}

export function clear() {
  entries.clear();
  owner = null;
  generation += 1;
}

/**
 * Runs `fetcher` and caches what `pick` takes from its result (default: the result itself; null
 * or undefined is not cached). Resolves or rejects exactly as the fetcher does. `accept(current,
 * next)` may refuse to replace a newer cached value with an older one.
 */
export async function remember(key, fetcher, pick = (x) => x, accept = null) {
  const gen = generation;
  const [who, result] = await Promise.all([currentOwner(), fetcher()]);
  put(key, pick(result), who, gen, accept);
  return result;
}
